import log from "loglevel";

const LOG_CHANNELS = [
  "appsync",
  "bootstrap",
  "call",
  "db",
  "electron",
  "events",
  "feed",
  "query",
  "recording",
  "reset",
  "storage",
  "telemetry",
  "upload",
  "version",
  "warmup",
] as const;

export type LogChannel = (typeof LOG_CHANNELS)[number];

const LEVEL_NAMES: Record<number, string> = {
  0: "TRACE",
  1: "DEBUG",
  2: "INFO",
  3: "WARN",
  4: "ERROR",
  5: "SILENT",
};

function prefixMethodFactory(
  methodName: log.LogLevelNames,
  level: log.LogLevelNumbers,
  loggerName: string | symbol,
): log.LoggingMethod {
  const raw = log.methodFactory(methodName, level, loggerName);
  return (...args: Parameters<log.LoggingMethod>) => {
    raw(`[${String(loggerName)}]`, ...args);
  };
}

const loggers = new Map<LogChannel, log.Logger>();

// Channels that default quieter than the global VITE_LOG_LEVEL. The query channel is torrential
// at DEBUG — every repository query build and reactive execution — and its timing data already
// flows through the flight recorder, so it stays quiet unless a persisted per-channel level
// (loglevel's own `loglevel:<channel>` localStorage key) turns it up.
const CHANNEL_DEFAULT_LEVELS: Partial<Record<LogChannel, log.LogLevelDesc>> = {
  query: "WARN",
};

export function createLogger(channel: LogChannel): log.Logger {
  const existing = loggers.get(channel);
  if (existing) return existing;

  const logger = log.getLogger(channel);
  const defaultLevel = CHANNEL_DEFAULT_LEVELS[channel];
  if (defaultLevel !== undefined) {
    logger.setDefaultLevel(defaultLevel);
  }
  logger.methodFactory = prefixMethodFactory;
  logger.rebuild();
  loggers.set(channel, logger);
  return logger;
}

export function initializeLogChannels(): void {
  for (const channel of LOG_CHANNELS) {
    createLogger(channel);
  }
}

export { LOG_CHANNELS };
